// Copyright 2025, University of Maryland, All Rights Reserved
interface SecondaryObjective {
  objective_statement: string;
}

interface LearningObjective {
  objective_statement: string;
  bloom_level?: string;
  secondary_objectives?: SecondaryObjective[];
}

interface LearningObjectivesProps {
  objectives: LearningObjective[];
}

export function LearningObjectives({ objectives }: LearningObjectivesProps) {
  if (!objectives || objectives.length === 0) {
    return null;
  }

  const getBloomLevelColor = (bloomLevel?: string) => {
    if (!bloomLevel) return 'bg-gray-100 text-gray-800';

    const level = bloomLevel.toLowerCase();
    if (level.includes('remember') || level.includes('remembering')) {
      return 'bg-purple-100 text-purple-800';
    } else if (level.includes('understand') || level.includes('understanding')) {
      return 'bg-blue-100 text-blue-800';
    } else if (level.includes('apply') || level.includes('applying')) {
      return 'bg-green-100 text-green-800';
    } else if (level.includes('analyze') || level.includes('analyzing')) {
      return 'bg-yellow-100 text-yellow-800';
    } else if (level.includes('evaluate') || level.includes('evaluating')) {
      return 'bg-orange-100 text-orange-800';
    } else if (level.includes('create') || level.includes('creating')) {
      return 'bg-red-100 text-red-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white p-8 rounded-xl shadow-lg pdf-page-break-avoid">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
        <span className="text-3xl mr-3">🎯</span>
        Learning Outcomes ({objectives.length})
      </h2>

      <div className="space-y-6">
        {objectives.map((objective, index) => (
          <div key={index} className="bg-gray-50 p-6 rounded-lg">
            {/* Primary Objective */}
            <div className="mb-4">
              <div className="flex items-start justify-between gap-4">
                <h3 className="text-lg font-semibold text-gray-900 flex-1">
                  {index + 1}. {objective.objective_statement}
                </h3>
                {objective.bloom_level && (
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getBloomLevelColor(objective.bloom_level)}`}>
                    {objective.bloom_level}
                  </span>
                )}
              </div>
            </div>

            {/* Secondary Objectives */}
            {objective.secondary_objectives && objective.secondary_objectives.length > 0 && (
              <div className="mt-4 ml-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Secondary Outcomes:</h4>
                <div className="space-y-2">
                  {objective.secondary_objectives.map((secondary, secIdx) => (
                    <div key={secIdx} className="bg-white p-3 rounded-lg">
                      <p className="text-sm text-gray-800">
                        {index + 1}.{secIdx + 1} {secondary.objective_statement}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
